import React, { useCallback, useRef, useMemo, useState, useEffect } from "react";
import { GoogleMap, LoadScriptNext, OverlayView } from "@react-google-maps/api";
import {
  Box,
  useColorModeValue,
  Image,
  Text,
  Flex,
  Icon,
  Modal,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  SlideFade,
  ModalOverlay,
  Badge,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  IconButton,
  useDisclosure,
  Spinner,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  Button,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
} from "@chakra-ui/react";
import { FaStar, FaMapMarkerAlt, FaClock } from "react-icons/fa";
import { FaList, FaTimes, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FaCamera, FaUserAlt, FaBuilding, FaFilter } from 'react-icons/fa';
import PhotographerPreviewModal from "../components/PhotographerPreviewModal";
import PhotographerList from "../components/PhotographerList2";
import FocusLock from "react-focus-lock"; // ✅ Import FocusLock

const containerStyle = {
  width: "100%",
  height: "calc(100dvh - 120px)", // ✅ Keeps the map below the search bar
  position: "absolute",
  top: 0,
  left: 0,
  zIndex: 0,
  overflow: "hidden",
};

const GoogleMapComponent = ({ searchLocation, photographers }) => {
  const mapRef = useRef(null);
  const filterBoxRef = useRef(null);
  const drawerRef = useRef(null);
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);
  const [isMiniModalOpen, setIsMiniModalOpen] = useState(false);
  const [isFullModalOpen, setIsFullModalOpen] = useState(false);
  const [filters, setFilters] = useState({ userType: null, minPrice: 0, maxPrice: 500 });
  const [isFilterBoxOpen, setIsFilterBoxOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 500]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState(searchLocation); // ✅ Track map center
  const [showSearchButton, setShowSearchButton] = useState(false); // ✅ Show "Search This Area" button
  const [selectedStyle, setSelectedStyle] = useState(""); // ✅ Track selected photography style
  const [selectedImage, setSelectedImage] = useState(null); // globally managed image modal


  // Memoize default center
  const defaultCenter = useMemo(() => searchLocation || { lat: 25.7617, lng: -80.1918 }, [searchLocation]);

  // Filter photographers based on filters
  const filteredPhotographers = useMemo(() => {
    return photographers.filter((photographer) => {
      const matchesUserType = !filters.userType || photographer.user_type === filters.userType;
      const matchesMinPrice = photographer.price >= filters.minPrice;
      const matchesMaxPrice = photographer.price <= filters.maxPrice || filters.maxPrice === 500; // ✅ Include prices 500 or more
      const matchesStyle = !selectedStyle || photographer.photography_style?.toLowerCase().includes(selectedStyle.toLowerCase());
      return matchesUserType && matchesMinPrice && matchesMaxPrice && matchesStyle;
    });
  }, [photographers, filters, selectedStyle]);

  // Memoize photographer markers
  const markers = useMemo(() => {
    return filteredPhotographers.map((photographer) => ({
      id: photographer.id,
      position: {
        lat: parseFloat(photographer.latitude),
        lng: parseFloat(photographer.longitude),
      },
      ...photographer, // ✅ Spread all photographer details
    }));
  }, [filteredPhotographers]);

  // Center the map on load
  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // 🎨 Styling
  const bgColor = useColorModeValue("white", "black");
  const textColor = useColorModeValue("black", "white");
  const modalTextColor = useColorModeValue("white", "black");
  const shadow = useColorModeValue("lg", "dark-lg");
  const filterBoxBg = useColorModeValue("white", "black"); // ✅ Dynamic background color
  const filterTextColor = useColorModeValue("black", "white"); // ✅ Dynamic text color
  const drawerBg = useColorModeValue("white", "blackAlpha.900"); // ✅ Define drawerBg for Drawer background

  function toTitleCase(str) {
    if (!str) return ""; // ✅ Return an empty string if input is undefined or null
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Function to update filters
  const updateFilters = (newFilters) => {
    setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
  };

  // Function to open the filter box
  const openFilterBox = () => {
    setIsFilterBoxOpen(true); // ✅ Open the filter box
  };

  // Function to close the filter box
  const closeFilterBox = () => {
    setIsFilterBoxOpen(false); // ✅ Close the filter box
  };

  // Function to toggle filter box
  const toggleFilterBox = () => {
    setIsFilterBoxOpen((prev) => {
      if (prev) {
        closeFilterBox(); // ✅ Call closeFilterBox when closing the filter box
      } else {
        openFilterBox(); // ✅ Call openFilterBox when opening the filter box
      }
      return !prev; // Toggle the state
    });
  };

  // Function to update price range filter
  const updatePriceRange = (range) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      minPrice: range[0], // ✅ Update minPrice
      maxPrice: range[1], // ✅ Update maxPrice
    }));
  };
  

  // Filter photographers for the Drawer list
  const filteredPhotographersForDrawer = useMemo(() => {
    return photographers.filter((photographer) => {
      const matchesUserType = !filters.userType || photographer.user_type === filters.userType;
      const matchesMinPrice = photographer.price >= filters.minPrice;
      const matchesMaxPrice = photographer.price <= filters.maxPrice;
      const matchesStyle = !selectedStyle || photographer.photography_style?.toLowerCase().includes(selectedStyle.toLowerCase()); // ✅ Check if selectedStyle is in photography_styles string
      return matchesUserType && matchesMinPrice && matchesMaxPrice && matchesStyle;
    });
  }, [photographers, filters, selectedStyle]);

  // Debugging: Log when the component mounts
  useEffect(() => {
    console.log("GoogleMapComponent mounted");
  }, []);

  // Debugging: Log the state of `isOpen` to ensure it updates correctly
  useEffect(() => {
    console.log("Drawer isOpen state:", isOpen);
  }, [isOpen]);

  // Update map center when searchLocation changes
  useEffect(() => {
    if (searchLocation && mapRef.current) {
      mapRef.current.panTo(searchLocation); // ✅ Pan the map to the new location
      setMapCenter(searchLocation); // ✅ Update the map center state
      console.log("Map center updated to:", searchLocation); // Debugging
    }
  }, [searchLocation]);

  // Handle map drag end
  const handleDragEnd = () => {
    if (mapRef.current) {
      const newCenter = mapRef.current.getCenter();
      setMapCenter({ lat: newCenter.lat(), lng: newCenter.lng() }); // ✅ Update map center
      setShowSearchButton(true); // ✅ Show the search button
      console.log("Map moved. New center:", newCenter.lat(), newCenter.lng()); // Debugging
    }
  };

  // Handle search for the new location
  const handleSearchThisArea = async () => {
    setLoading(true); // ✅ Show spinner
    setShowSearchButton(false); // ✅ Hide the button while loading

    // Simulate a search request (replace with actual API call)
    setTimeout(() => {
      console.log("Searching for photographers at:", mapCenter);

      // ✅ Pan the map to the new center
      if (mapRef.current) {
        mapRef.current.panTo(mapCenter);
        console.log("Map panned to new center:", mapCenter);
      }

      setLoading(false); // ✅ Hide spinner after loading
    }, 2000);
  };

  return (
    <>
      <LoadScriptNext googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter || defaultCenter} // ✅ Use dynamic map center with fallback to defaultCenter
          zoom={12}
          onLoad={(map) => {
            mapRef.current = map;
            console.log("Map loaded:", map); // Debugging
          }}
          onDragEnd={handleDragEnd} // ✅ Detect when the user stops dragging the map
          options={{
            mapTypeControl: false,
            fullscreenControl: false,
            zoomControl: true,
            zoomControlOptions: {
              position: window.google?.maps?.ControlPosition?.TOP_RIGHT,
            },
            streetViewControl: true,
            streetViewControlOptions: {
              position: window.google?.maps?.ControlPosition?.TOP_RIGHT,
            },
            gestureHandling: "greedy",
            disableDefaultUI: true,
          }}
        >
          {/* ✅ Custom Markers */}
          {markers.map((marker) => (
            <OverlayView
              key={marker.id}
              position={marker.position}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <Box
                bg={bgColor}
                color={textColor}
                px={3}
                py={1}
                borderRadius="full"
                fontWeight="bold"
                fontSize="sm"
                boxShadow={shadow}
                border="1px solid"
                borderColor="gray.300"
                whiteSpace="nowrap"
                display="inline-block"
                cursor="pointer"
                _hover={{ transform: "scale(1.1)", transition: "0.2s" }}
                onClick={() => {
                  setSelectedPhotographer(marker);
                  setIsMiniModalOpen(true); // ✅ Opens Mini Modal
                }}
              >
                ${marker.price}/hr
              </Box>
            </OverlayView>
          ))}
        </GoogleMap>
      </LoadScriptNext>

      {/* "Search This Area" Button */}
      {showSearchButton && !loading && (
        <Button
          position="absolute"
          bottom="80px"
          left="50%"
          transform="translateX(-50%)"
          zIndex="2"
          bg="purple.500"
          color="white"
          borderRadius="md"
          boxShadow="lg"
          onClick={handleSearchThisArea}
          _hover={{ bg: "purple.600" }}
          style={{ border: "1px solid red" }} // Temporary border for debugging
        >
          Search This Area
        </Button>
      )}

      {/* Loading Spinner */}
      {loading && (
        <Box
          position="absolute"
          bottom="80px"
          left="50%"
          transform="translateX(-50%)"
          zIndex="2"
        >
          <Spinner size="lg" color="purple.500" />
        </Box>
      )}

      {/* ✅ Mini Preview Modal (Opens When Marker Clicked) */}
      <Modal
        isOpen={isMiniModalOpen}
        onClose={() => setIsMiniModalOpen(false)}
        motionPreset="slideInBottom"
      >
        <ModalContent
          position="fixed"
          bottom="1%" // ✅ Keeps it in the lower middle
          transform="translateX(-50%)"
          width={{ base: "90%", md: "80%", lg: "60%" }}
          maxW="400px"
          borderRadius="xl"
          overflow="hidden"
          boxShadow="xl"
          p={0}
          height="140px"
          onClick={() => {
            setIsMiniModalOpen(false);
            setIsFullModalOpen(true); // ✅ Opens Full Booking Modal
          }}
        >
          <ModalCloseButton />
          <ModalBody p={0} bg={bgColor}>
            <SlideFade in={isMiniModalOpen} offsetY="20px">
              <Flex align="stretch">
                {/* ✅ Square Image */}
                <Box flex="1" width="140px" height="140px">
                  <Image
                    src={selectedPhotographer?.avatar}
                    alt={selectedPhotographer?.name}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                </Box>

                {/* ✅ Text Content */}
                <Box p={3} flex="2" display="flex" flexDirection="column" justifyContent="center" color={textColor}>
                  <Text fontWeight="bold" fontSize="md">{selectedPhotographer?.name}</Text>

                  <Flex align="center" mt={1}>
                    <Icon
                      as={
                        selectedPhotographer?.user_type === "photographer" || selectedPhotographer?.user_type === "videographer"
                          ? FaCamera
                          : selectedPhotographer?.user_type === "model"
                          ? FaUserAlt
                          : selectedPhotographer?.user_type === "studio"
                          ? FaBuilding
                          : FaStar // Default icon if user_type is not recognized
                      }
                      color="purple.400"
                      mr={1}
                      boxSize={3}
                    />
                    <Text fontSize="xs">{toTitleCase(selectedPhotographer?.user_type)}</Text>
                  </Flex>

                  <Flex align="center" mt={1}>
                    <Icon as={FaMapMarkerAlt} color="purple.400" mr={1} boxSize={3} />
                    <Text fontSize="xs">{selectedPhotographer?.location || "Unknown"}</Text>
                  </Flex>

                  <Flex align="center" mt={1}>
                    <Icon as={FaClock} color="green.400" mr={1} boxSize={3} />
                    <Text fontSize="xs">
                      {selectedPhotographer?.start_time} - {selectedPhotographer?.end_time}
                    </Text>
                  </Flex>
                  <Flex mt={3} justify="space-between" align="center">
                    <Badge colorScheme="purple" fontSize="md">
                      ${selectedPhotographer?.price}/Hour
                    </Badge>
                  </Flex>
                </Box>
              </Flex>
            </SlideFade>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* ✅ Full Photographer Booking Modal (Opens When Mini Modal Clicked) */}
      <PhotographerPreviewModal
        isOpen={isFullModalOpen}
        onClose={() => setIsFullModalOpen(false)}
        creator={selectedPhotographer}
        setSelectedImage={setSelectedImage} // new prop
      />


      
      {/* Open List Button (Floating) */}
      <IconButton
        aria-label={isOpen ? "Hide Photographers" : "Show Photographers"} // ✅ Update aria-label for accessibility
        icon={isOpen ? <FaTimes /> : <FaList />} // ✅ Change icon based on Drawer state
        position="fixed"
        bottom="16px"
        right="16px"
        size="lg"
        colorScheme="purple"
        onClick={() => {
          console.log("IconButton clicked, toggling Drawer state"); // ✅ Debugging: Log click event
          isOpen ? onClose() : onOpen(); // ✅ Explicitly toggle Drawer state
        }}
        shadow="lg"
        borderRadius="full"
      />

      {/* Drawer for Photographer List */}
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        placement="bottom"
        closeOnOverlayClick={false} // ✅ Important
      >


        <DrawerOverlay />
        <DrawerContent
          ref={drawerRef} // ✅ Attach ref to the Drawer
          bg={drawerBg}
          borderTopRadius="lg"
          maxH="80vh"
          minH="50vh"
          overflow="hidden"
        >
          {/* Drawer Header with Expand/Collapse Arrow */}
          <DrawerHeader textAlign="center" cursor="pointer" onClick={() => {
            console.log("DrawerHeader clicked, toggling Drawer state"); // ✅ Debugging: Log click event
            isOpen ? onClose() : onOpen();
          }}>
          <Flex justify="center" align="center">
            <Icon as={isOpen ? FaChevronDown : FaChevronUp} boxSize={4} mr={2} />
            <Text fontSize="md" fontWeight="medium">
              {isOpen ? "Hide List" : "Expand List"}
            </Text>
          </Flex>
          </DrawerHeader>
          <DrawerCloseButton />

          {/* Drawer Body with Photographer List */}
          <DrawerBody overflowY="auto">
            {loading ? (
              <Box textAlign="center">
                <Spinner size="xl" />
              </Box>
            ) : (
              <PhotographerList photographers={filteredPhotographersForDrawer} />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Filter Box as a Popover */}
      <Popover
        isOpen={isFilterBoxOpen} // ✅ Control the popover state
        onClose={() => setIsFilterBoxOpen(false)} // ✅ Close when clicking outside or close button
        placement="bottom-start" // ✅ Position the popover below and aligned with the button
        closeOnBlur={true} // ✅ Allow closing on outside click
        returnFocusOnClose={false} // ✅ Prevent returning focus to the toggle button
      >
        <PopoverTrigger>
          <Button
            id="filter-toggle-button" // ✅ Add an ID to identify the toggle button
            position="absolute"
            top="20px"
            left="20px"
            zIndex="2"
            colorScheme="purple"
            borderRadius="full"
            boxShadow="lg"
            p={4}
            onClick={() => {
              console.log("Filter button clicked, toggling filter box state"); // ✅ Debugging: Log click event
              setIsFilterBoxOpen((prev) => !prev); // ✅ Toggle filter box state
            }}
            _hover={{ bg: "purple.600" }}
          >
            <Icon as={FaFilter} boxSize={5} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          bg={filterBoxBg} // ✅ Dynamic background color
          color={filterTextColor} // ✅ Dynamic text color
          p={4}
          borderRadius="md"
          boxShadow="lg"
          width="300px"
          maxW="90%" // ✅ Ensure responsiveness
          onClick={(e) => e.stopPropagation()} // ✅ Prevent closing when interacting with the content
        >
          <FocusLock returnFocus persistentFocus={false}> {/* ✅ Lock focus within the popover */}
            <PopoverArrow /> {/* ✅ Arrow pointing to the button */}
            <PopoverCloseButton /> {/* ✅ Close button */}
            <Text fontWeight="bold" mb={3} fontSize="lg" textAlign="center">
              Filters
            </Text>
            <Flex direction="column" gap={4}>
              {/* User Type Filter */}
              <select
                value={filters.userType || ""} // ✅ Bind value to filters.userType
                onChange={(e) => {
                  e.stopPropagation(); // ✅ Prevent popover from closing
                  updateFilters({ userType: e.target.value || null });
                }}
                onBlur={(e) => e.stopPropagation()} // ✅ Prevent blur event from closing the popover on iOS
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  backgroundColor: filterBoxBg,
                  color: filterTextColor,
                }}
              >
                <option value="">All Types</option>
                <option value="photographer">Photographer</option>
                <option value="videographer">Videographer</option>
                <option value="model">Model</option>
                <option value="studio">Studio</option>
              </select>

              {/* Price Range Filter Slider */}
              <Box>
                <Text mb={2} fontSize="sm">
                  Price Range: ${priceRange[0]} - ${priceRange[1] === 500 ? "500+" : priceRange[1]} {/* ✅ Show "500+" for max */}
                </Text>
                <RangeSlider
                  value={priceRange} // ✅ Bind slider value to state
                  min={0}
                  max={500}
                  step={10}
                  onChange={(range) => {
                    setPriceRange(range); // ✅ Update state on slider movement
                  }}
                  onChangeEnd={(range) => {
                    setPriceRange(range); // ✅ Ensure state is updated
                    updatePriceRange(range); // ✅ Update filters when slider movement ends
                  }}
                >
                  <RangeSliderTrack bg="purple.200">
                    <RangeSliderFilledTrack bg="purple.600" />
                  </RangeSliderTrack>
                  <RangeSliderThumb boxSize={6} bg="purple.800" index={0} />
                  <RangeSliderThumb boxSize={6} bg="purple.800" index={1} />
                </RangeSlider>
              </Box>

              {/* Photography Style Filter */}
              <select
                value={selectedStyle} // ✅ Bind value to selectedStyle
                onChange={(e) => {
                  e.stopPropagation(); // ✅ Prevent popover from closing
                  setSelectedStyle(e.target.value);
                }}
                onBlur={(e) => e.stopPropagation()} // ✅ Prevent blur event from closing the popover on iOS
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  backgroundColor: filterBoxBg,
                  color: filterTextColor,
                }}
              >
                <option value="">All Styles</option>
                <option value="street">Street</option>
                <option value="boudoir">Boudoir</option>
                <option value="weddings">Weddings</option>
                <option value="events">Events</option>
                <option value="editorial">Editorial</option>
                <option value="last minute">Last Minute</option>
                <option value="social media">Social Media</option>
                <option value="real estate">Real Estate</option>
              </select>
            </Flex>
          </FocusLock>
        </PopoverContent>
      </Popover>
    {/* ✅ Lightbox Modal for Full Image View - moved OUTSIDE of PhotographerPreviewModal */}
    <Modal isOpen={!!selectedImage} onClose={() => setSelectedImage(null)}>
      <ModalOverlay />
      <ModalContent
        maxW="420px"
        maxH="500px"
        display="flex"
        justifyContent="center"
        alignItems="center"
        bg={bgColor}
        borderRadius="lg"
        boxShadow="2xl"
        p={4}
        position="fixed"
        top="15%"
        left="50%"
        transform="translate(-50%, -50%)"
        zIndex="modal"
      >
        <ModalCloseButton color={textColor} size="lg" />
        <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
          {selectedImage && (
            <Box borderRadius="md" overflow="hidden">
              <Image
                src={selectedImage}
                alt="Selected Photo"
                maxH="450px"
                maxW="450px"
                objectFit="contain"
                borderRadius="md"
              />
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>

    </>
  );
};

export default GoogleMapComponent;
